import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, filter, map, take, takeUntil, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { SnCurrentOrganization } from '@lib/auth/services';

import { AxonVILprService } from '../../integrations';
import { ViLPRProvider } from '../../shared/models';
import { getBoundingBox } from '../../utils/utils';
import { LPR_ERRORS, LprWarningEntry } from '../constants/errors';
import { PlateResultSort } from '../constants/parameters';
import { US_STATES_VALUES_TO_CODES } from '../constants/parameters';
import { IFsHistoricalProviderResultsInterface, LPRResultsFacets, LPRSharingDataScope } from '../interfaces';
import { IFsLprParameters } from '../interfaces/common/parameters';
import { IFsLprProviderInterface } from '../interfaces/common/provider';
import {
  AxonVISortOrder,
  IFsLprAxonVIParameters,
  RECORD_TYPE_TO_AXON_VI_RECORD_TYPE,
} from '../interfaces/lpr-axon-vi-parameters.interface';
import { FsLprAxonVIResults } from '../interfaces/lpr-axon-vi-results';
import { FsLprAxonVIRead } from '../models';

// Fleet requires searchReason, so Partial<> needs to keep it required
export type PartialAxonVIParams = Partial<Omit<IFsLprAxonVIParameters, 'searchReason'>> & {
  searchReason: string;
};

// TODO: Remove this once we are using the filters endpoint to populate the camera models
export const CAMERA_MODELS = {
  fleet_3: ['axon_fleet_3_hub'],
  outpost: ['axon_outpost'],
  lightpost: ['axon_lightpost'],
};

export const SHARED_PROVIDER_NAME = 'Shared';

@Injectable({ providedIn: 'root' })
export class AxonVIProviderService implements IFsLprProviderInterface {
  public name: string;
  public nameEnum?: ViLPRProvider;

  public get loaded$(): Observable<boolean> {
    return this._loaded$;
  }

  public errors: string[] = [];
  public warnings: LprWarningEntry[] = [];

  public isLoading: boolean;
  public isInit: boolean;
  public isFinished: boolean = false;
  public total: number;

  private _params: IFsLprAxonVIParameters;
  private _loaded$: Subject<boolean> = new Subject<boolean>();

  private _inited$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private readonly _destroy$: Subject<void> = new Subject<void>();
  private readonly _cancel$: Subject<void> = new Subject<void>();

  private _lastSortValues: string | null = null;

  constructor(
    public readonly _axonVILprService: AxonVILprService,
    name: string,
    private readonly _currentOrganization: SnCurrentOrganization,
  ) {
    this.name = name;
    if (this._sdmEnabled) {
      this.nameEnum = ViLPRProvider.AXON_VI;
      this.isLoading = true;
      this.isInit = false;
      this.total = 0;
    }
  }

  historicLoaded$: Observable<boolean>;

  public results: FsLprAxonVIRead[] = [];
  public facets: LPRResultsFacets;

  public init(): void {
    this.isInit = true;
    this._inited$.next(true);
  }

  public search(query: Partial<IFsLprParameters>): void {
    const inited$ = this._sdmEnabled
      ? this._inited$.pipe(
          filter((inited) => inited),
          take(1),
        )
      : this._inited$;
    inited$.subscribe((inited) => {
      if (inited) {
        this._startSearch(query);
      }
    });
  }

  public next(): void {
    // Check if we have results to get sortValues from
    if (this.results.length === 0) {
      this.isFinished = true;
      this.isLoading = false;
      this._loaded$.next(true);
      return;
    }

    const newSortValues = this.results[this.results.length - 1].sortValues.toString();

    // Detect duplicate sortValues (stuck on same page)
    if (this._lastSortValues === newSortValues) {
      this.isFinished = true;
      this.isLoading = false;
      this._loaded$.next(true);
      return;
    }

    this._lastSortValues = newSortValues;
    this.isLoading = true;
    if (this._sdmEnabled) {
      this._loaded$.next(false);
    }
    this._params.sortValues = newSortValues;

    this._axonVILprService
      .getAxonVIPlates(this._params)
      .pipe(
        takeUntil(this._cancel$),
        catchError(() => {
          this.errors.push(LPR_ERRORS.FAILED_TO_LOAD_NEXT_PAGE);
          this.isLoading = false;
          this.isFinished = true;
          this._loaded$.next(true);
          return of(null);
        }),
      )
      .subscribe(this._handleResponse.bind(this));
  }

  public getHistoricalResults(
    searchParams: Partial<IFsLprParameters>,
  ): Observable<IFsHistoricalProviderResultsInterface> {
    // if the provider is Shared and includeSharedData is false, do nothing
    if (this.name === SHARED_PROVIDER_NAME && !searchParams.includeSharedData) {
      return of(null);
    }

    const modifiedParams = this._historicalSearchTransformParams(searchParams);

    return this._axonVILprService.getAxonVIPlates(modifiedParams).pipe(
      map((response: FsLprAxonVIResults) => {
        return {
          results: response.data || [],
          total: response.metadata.totalQueryResults || 0,
        };
      }),
      catchError(() => {
        return of(null);
      }),
      takeUntil(this._destroy$),
    );
  }

  public cancel(): void {
    this._cancel$.next();
  }

  // TODO: Update this to return FsLprAxonVIRead once backend is updated
  public getReadById(
    readId: string,
    timestamp: number,
    searchReason: string,
  ): Observable<FsLprAxonVIRead> {
    if (this._sdmEnabled) {
      this.isLoading = true;
      this._loaded$.next(false);
      return this._axonVILprService.getRead({ readId, timestamp, searchReason }).pipe(
        tap((read) => {
          this.results.push(read);
          this.total = 1;
          this.isLoading = false;
          this.isFinished = true;
          this.errors = [];
          this._loaded$.next(true);
        }),
        catchError(() => {
          this.errors.push(LPR_ERRORS.FAILED_TO_LOAD_READ);
          this.total = 0;
          this.isLoading = false;
          this.isFinished = true;
          this._loaded$.next(true);
          return throwError(() => new Error(LPR_ERRORS.FAILED_TO_LOAD_READ));
        }),
      );
    }
    return this._axonVILprService.getRead({ readId, timestamp, searchReason });
  }

  private get _sdmEnabled(): boolean {
    return this._currentOrganization?.settings?.sourceAndDeviceManagementEnabled ?? false;
  }

  private _startSearch(query: Partial<IFsLprParameters>): void {
    this.cancel(); // Cancel any ongoing search

    // Reset all state for new search
    this.isFinished = false;
    this.errors = [];
    this.warnings = [];
    this.results = [];
    this.isLoading = true;
    if (this._sdmEnabled) {
      this._loaded$.next(false);
    }
    // Searching should refetch facets
    this.facets = null;

    // Reset infinite loop protection counters
    this._lastSortValues = null;

    const params = this._searchTransformParams(query);
    if (this.errors.length > 0 || (this.name === SHARED_PROVIDER_NAME && !query.includeSharedData)) {
      this.isLoading = false;
      this.isFinished = true;
      this.total = 0;
      this._loaded$.next(true);
      return;
    }
    this._axonVILprService
      .getAxonVIPlates(params)
      .pipe(
        takeUntil(this._cancel$),
        catchError(() => {
          this.errors.push(LPR_ERRORS.FAILED_TO_LOAD_SEARCH_RESULTS);
          this.isLoading = false;
          this.isFinished = true;
          this.total = 0;
          this._loaded$.next(true);
          return of(null);
        }),
      )
      .subscribe(this._handleResponse.bind(this));
  }

  private _handleResponse(response: FsLprAxonVIResults): void {
    /**
     * Having BE return facets is a costly operation, so we only want it to happen when doing "full searches"
     * The providers can either fetch reads by calling search() or next()
     * - search() is called when doing a new search with new parameters, and should include facets
     * - next() is called when fetching the next page of results for an existing search, and should NOT include facets
     */
    this._params.includeFacets = false;

    if (response) {
      this.facets = this.facets || response.metadata?.facets;

      const newDataCount = response.data?.length || 0;
      if (newDataCount === 0) {
        this.isFinished = true;
        this.isLoading = false;
        // Update total to reflect actual available data
        this.total = this.results.length;
        this._loaded$.next(true);
        return;
      }

      // Set total, but handle null/undefined gracefully
      this.total = response.metadata?.totalQueryResults ?? this.results.length + newDataCount;

      this.results = [...this.results, ...response.data];
      this.isFinished = this.results.length >= this.total || this.total === 0;

      this.isLoading = false;
      this._loaded$.next(true);
    }
  }

  // TODO: Update params with axon-vi camera parameter
  // https://taserintl.atlassian.net/browse/FWX-2222
  private _baseTransformParams(params: Partial<IFsLprParameters>): PartialAxonVIParams {
    const {
      includeFacets,
      searchReason,
      plate,
      fromDate: _fromDate,
      toDate: _toDate,
      limit: _limit,
      plateState,
      vehicleMake,
      vehicleBody,
      vehicleName,
      vehicleColor: _vehicleColor,
      inBbox: _inBbox,
      recordType: _recordType,
      assignedUserIds: _assignedUserIds,
      deviceNames: _deviceNames,
      includeSharedData,
      sort: _sort,
      topRack,
      windowStickers,
      bumperStickers,
    } = params;

    const plateStateCode = US_STATES_VALUES_TO_CODES.find(
      (state) => state.value === plateState?.toLowerCase(),
    )?.code;
    const cameraModels = this._sdmEnabled
      ? params.cameraModels
      : this.name
      ? this._convertProviderNameToCameraModel(this.name)
      : undefined

    const baseParams: PartialAxonVIParams = {
      includeFacets,
      searchReason,
      plate,
      cameraModels,
      cameraVendors: this._sdmEnabled ? params.cameraVendors : undefined,
      fromDate: _fromDate?.toISOString(),
      toDate: _toDate?.toISOString(),
      limit: _limit?.toString(),
      sort:
        _sort === PlateResultSort.ASC
          ? AxonVISortOrder.CAPTURE_TIME_ASC
          : AxonVISortOrder.CAPTURE_TIME_DESC,
      recordType: RECORD_TYPE_TO_AXON_VI_RECORD_TYPE[_recordType],
      vehicleMake,
      vehicleBody,
      vehicleName,
      dataScope: this._getDataScopeParam(cameraModels, includeSharedData),
      ...(_inBbox && {
        inBbox: getBoundingBox(
          _inBbox,
          this._sdmEnabled && this.nameEnum ? this.nameEnum : this.name,
        ),
      }),
      ...(plateState && { subdivisionCode: plateStateCode }),
      ...(topRack && { topRack }),
      ...(windowStickers && { windowStickers }),
      ...(bumperStickers && { bumperStickers }),
    };

    // Fleet API requires vehicle color as a comma separated string, not an array
    if (Array.isArray(_vehicleColor) && _vehicleColor.length) {
      baseParams.vehicleColor = _vehicleColor.join(',');
    }

    if (Array.isArray(_assignedUserIds) && _assignedUserIds.length > 0) {
      baseParams.assignedUserIds = _assignedUserIds.join(',');
    }

    if (Array.isArray(_deviceNames) && _deviceNames.length > 0) {
      baseParams.cameraNames = _deviceNames.join(',');
    }

    return baseParams;
  }

  private _searchTransformParams(params: Partial<IFsLprParameters>): IFsLprAxonVIParameters {
    this._params = this._baseTransformParams(params);

    return this._params;
  }

  private _historicalSearchTransformParams(
    params: Partial<IFsLprParameters>,
  ): IFsLprAxonVIParameters {
    return this._baseTransformParams(params);
  }

  /**
   * This function is used to convert the provider name to a camera model string
   * @param providerName - The name of the provider
   * @returns The camera model string to be used in the api call
   */
  private _convertProviderNameToCameraModel(providerName: string): string {
    if (this._sdmEnabled) {
      return undefined; // Not needed when SDM enabled
    }

    if (providerName === SHARED_PROVIDER_NAME) {
      return undefined;
    }

    const providerNameLower = providerName.toLowerCase().replace(' ', '_');
    const cameraModels = CAMERA_MODELS[providerNameLower];
    return cameraModels ? cameraModels.toString() : providerNameLower;
  }

  private _getDataScopeParam(cameraModels: string, includeSharedData: boolean): LPRSharingDataScope {
    // If Shared provider, search for shared data only
    if (this.name === SHARED_PROVIDER_NAME) {
      return LPRSharingDataScope.SHARED;
    }

    // If no camera models are selected, search for all data if includeSharedData is true, otherwise search for own data only
    if (!cameraModels) {
      return includeSharedData ? LPRSharingDataScope.ALL : LPRSharingDataScope.OWN;
    }

    // If camera models are selected, search for own data only
    return LPRSharingDataScope.OWN;
  }
}
